import React, { useEffect } from 'react';
import { useActivation } from '../../hooks/useActivation.hook';
import { useUser } from '../../contexts/userContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import {
  LoadingStep,
  CodeInputStep,
  CodeValidStep,
  CodeInvalidStep,
  ActivatingStep,
  SuccessStep,
  ErrorStep
} from '../../components/Activation';
import ActivationHeader from '../../components/Activation/ActivationHeader';
import Sign from '../../components/Sign/Sign';
import '../../Styles/Activation.scss';

const ActivatePage: React.FC = () => {
  const {
    state,
    validateCode,
    activateCode,
    resetActivation
  } = useActivation();

  const { state: userState, dispatch } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (userState.isLoggedIn && state.step === 'code-valid') {
      activateCode();
    }
  }, [userState.isLoggedIn, state.step, activateCode]);

  useEffect(() => {
    const updateSubscriptionStatus = async () => {
      if (state.step === 'success') {
        try {
          const response = await authService.checkSubscription();
          
          if (response.responseObject.activeSubscription === true) {
            dispatch({ type: 'set_subscribed', payload: true });
            dispatch({
              type: 'set_subscription_id',
              payload: response.responseObject.stripeInfo.stripeId
            });
            dispatch({ type: 'set_is_subscribed', payload: true });
          }

          if (response.responseObject.isTrial === true) {
            dispatch({ type: 'set_trial', payload: true });
          }
        } catch (error) {
          console.error('Failed to update subscription status:', error);
        }
      }
    };

    updateSubscriptionStatus();
  }, [state.step, dispatch]);

  const handleActivateClick = () => {
    if (!userState.isLoggedIn) {
      const returnUrl = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`/signup?returnUrl=${returnUrl}`);
      return;
    }
    activateCode();
  };

  const renderStep = () => {
    switch (state.step) {
      case 'loading':
        return <LoadingStep />;

      case 'enter-code':
      case 'validating':
        return (
          <CodeInputStep
            onValidate={validateCode}
            isLoading={state.isLoading}
            resellerName={state.resellerName}
            resellerCode={state.resellerCode}
            resellerLogoUrl={state.resellerLogoUrl}
          />
        );

      case 'code-valid':
        return (
          <CodeValidStep
            couponCode={state.fullCode}
            onActivate={handleActivateClick}
            onClearCode={resetActivation}
            resellerName={state.resellerName}
            resellerLogoUrl={state.resellerLogoUrl}
          />
        );

      case 'code-invalid':
        return (
          <CodeInvalidStep
            couponCode={state.couponCode}
            message={state.validationMessage}
            onTryAgain={resetActivation}
            onClearCode={resetActivation}
            resellerName={state.resellerName}
            resellerLogoUrl={state.resellerLogoUrl}
          />
        );

      case 'redeeming':
        return (
          <ActivatingStep
            resellerName={state.resellerName}
            resellerLogoUrl={state.resellerLogoUrl}
          />
        );

      case 'success':
        return (
          <SuccessStep
            userEmail={state.userEmail}
            userFirstName={state.userFirstName}
            userLastName={state.userLastName}
          />
        );

      case 'error':
        return (
          <ErrorStep
            error={state.error || 'An unexpected error occurred'}
            onRetry={resetActivation}
            resellerName={state.resellerName}
            resellerLogoUrl={state.resellerLogoUrl}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Sign 
      resellerLogo={state.resellerLogoUrl} 
      resellerName={state.resellerName}
      resellerCode={state.resellerCode}
    >
      <div className="activate-form">
        <ActivationHeader
          resellerLogoUrl={state.resellerLogoUrl}
          resellerName={state.resellerName}
          resellerCode={state.resellerCode}
        />
        {renderStep()}
      </div>
    </Sign>
  );
};

export default ActivatePage;
